import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DropdownInputsSignalService } from './dropdown-inputs-signal.service';
import { JwtService } from './jwt.service';
import { ModulesMenuService } from './modules-menu.service';

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private API_USERS_URL = `${environment.apiBaseUrl}/User`;

    private perms$ = new BehaviorSubject<string[]>([]);

    constructor(
        private http: HttpClient,
        private signalService: DropdownInputsSignalService,
        private jwtService: JwtService,
        private modulesMenuService: ModulesMenuService
    ) {
        // Listen for permission invalidation events
        this.signalService.reload$.subscribe((signal) => {
            if (signal === 'invalidatePermissions') {
                const userId = this.jwtService.getUserId(sessionStorage.getItem('accessToken') || '');
                if (userId) {
                    this.loadPermissions(userId, false);
                }
            }
        });
    }

    loadPermissions = (userId: string, getCachedPermissions: boolean = true) => {
        if(getCachedPermissions) {
            let permissions = sessionStorage.getItem('permissions');
            if (permissions === "undefined") permissions = '[]';

            const prevPremissions = JSON.parse(permissions ?? '[]') as string[];

            this.setPermissions(prevPremissions);
            this.signalService.triggerReload('permissions');
            this.modulesMenuService.loadModules();

            if (prevPremissions.length > 0) return;
        }

        this.http
            .get<string[]>(`${this.API_USERS_URL}/${userId}/Claims`)
            .subscribe({
                next: (list: any) => {
                    const perms: string[] = list.data.roles?.[0]?.claims.map((el: any) => el.value.toLowerCase()) ?? [];
                    this.fetchAllModules(perms);
                    this.signalService.triggerReload("permissions");
                },
                error: () => {
                    this.setPermissions([]);
                    this.signalService.triggerReload("permissions");
                }
            });
    }

    fetchAllModules(userPremssions: string[]) {
        // Use the centralized, cached my-modules loader so we only hit the API once per session.
        this.modulesMenuService.ensureModulesLoaded()
            .subscribe({
                next: (modulesList) => {
                    const modules: string[] = [];
                    (modulesList ?? []).forEach((el) =>
                        el.pages?.forEach((ele) =>
                            ele.actionList?.forEach((action) => {
                                const key = action.permissionKey?.toLowerCase();
                                if (key) modules.push(key);
                            })
                        )
                    );

                    // Store only permissions that exist in BOTH claims AND my-modules (intersection)
                    const result = userPremssions.filter(el => modules.includes(el.toLowerCase()));

                    this.setPermissions(result);
                    sessionStorage.setItem('permissions', JSON.stringify(result));
                    this.signalService.triggerReload('permissions');
                },
                error: () => {
                    // my-modules failed; intersection not applied until next successful load
                }
            });
    }


    setPermissions(perms: string[]) {
        this.perms$.next(perms);
    }

    getPermissions(): Observable<string[]> {
        return this.perms$.asObservable();
    }

    hasPermission(key: string): boolean {

        if(key === "allow") return true;
        // Check if user is admin by decoding the JWT token directly
        const token = sessionStorage.getItem('accessToken');
        if (token) {
            const user = this.jwtService.decodeToken(token);
            if (user?.is_admin === "True" || user?.is_admin === true) {
                return true;
            }
        }
        return this.perms$.getValue().includes(key.toLowerCase());
    }

    hasAny(keys: string[]): boolean {
        return keys.some(k => this.hasPermission(k));
    }
}
